import React, { useState, useEffect, useRef } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from 'app/components/Button/ButtonVariant';
import { FacilityProfile, Coach, Event, FacilityAPIResponse, CoachProfileAPI, ProgramAPI, transformFacilityResponse, transformCoachesResponse, transformProgramToEvent, getHighQualityImageUrl } from '../services/api';
import { schedulingFetchGet } from 'app/utils/request/scheduling/fetch';
import { isEmpty } from 'app/utils';
import default_logo_img from 'app/images/fallback/logo/square_default.png';
import default_profile_img from 'app/images/fallback/profiles/square_default.png';

import avatarDefault from '../assets/avatar.png';
import avatarAlina from '../assets/avatar-alina.png';
import avatarDavid from '../assets/avatar-david.png';
import facilityLogo from '../assets/facility-logo.png';
import coachNowLogo from '../assets/coachnow-small-logo.png';
import phoneIcon from '../assets/phone.svg';
import mailIcon from '../assets/mail.svg';
import linkIcon from '../assets/link.svg';
import locationIcon from '../assets/location.svg';
import timeIcon from '../assets/time.svg';
import moneyIcon from '../assets/money.svg';
import { fetchGet } from 'app/utils/request';
import settings from 'config/environment';
import {generateCurrencySymbol} from "app/routes/BuildProgram/Pricing/utils";
import { formatPhoneNumber } from 'app/routes/Contacts/utils';
import ProgramCard from "./ProgramCard";
import { hasFacilityBanner, hasFacilityLogo } from "./facilityBranding";

const AVATAR_MAP: { [key: string]: string } = {
  'avatar.png': avatarDefault,
  'avatar-alina.png': avatarAlina,
  'avatar-david.png': avatarDavid,
  'facility-logo.png': facilityLogo
};

// Helper function to format address from components
const formatAddress = (address?: string, city?: string, country?: string, zip?: string): string | undefined => {
  const parts = [address, city, country && zip ? `${country} ${zip}` : country || zip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : undefined;
};

// Helper functions for image fallbacks
const getFacilityLogo = (logoUrl: string | undefined): string => {
  if (!hasFacilityLogo(logoUrl)) {
    return default_logo_img;
  }
  // Check if it's a local asset
  const filename = logoUrl?.split('/').pop();
  if (filename && AVATAR_MAP[filename]) {
    return AVATAR_MAP[filename];
  }
  return logoUrl!;
};

export const getCoachAvatar = (avatarInput: string | undefined): string => {
  const avatarUrl = Array.isArray(avatarInput)
    ? avatarInput.find((a) => !!a) || undefined
    : avatarInput || undefined;

  if (!avatarUrl || isEmpty(avatarUrl) || avatarUrl.includes('/assets/fallback/profiles/')) {
    return default_profile_img;
  }

  const filename = avatarUrl.split('/').pop();
  if (filename && AVATAR_MAP[filename]) {
    return AVATAR_MAP[filename];
  }

  return getHighQualityImageUrl(avatarUrl) || avatarUrl;
};

interface FacilityPageProps {
  previewMode?: boolean;
  previewData?: {
    facility: {
      name: string;
      description: string;
      phone: string;
      email: string;
      website: string;
    };
    publicLogoUrl: string;
    publicCoverUrl: string;
    publicCalendarUrl?: string;
    publicProgramsUrl?: string;
    publicCalendar?: boolean;
    coaches?: Coach[];
    events?: Event[];
  };
}

const FacilityPage: React.FC<FacilityPageProps> = ({ previewMode = false, previewData }) => {
  const { t } = useTranslation();
  const { facilityId } = useParams<{ facilityName: string; facilityId: string }>();

  const [facility, setFacility] = useState<FacilityProfile | null>(null);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [coachProfiles, setCoachProfiles] = useState<CoachProfileAPI[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);
  /**
   * CFTB-3454: whether the description is actually clipped, which is the only case
   * "Read More" belongs in. The clamp is ten lines of CSS, so the answer is a
   * measurement rather than a character count — and it is remeasured on resize,
   * where the same text wraps to a different number of lines.
   */
  const aboutTextRef = useRef<HTMLParagraphElement>(null);
  const [aboutIsClamped, setAboutIsClamped] = useState(false);
  const [showAllCoaches, setShowAllCoaches] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const history = useHistory();

  const loadFacilityData = async (facilityIdParam: string) => {
    let facilityName = '';
    let facilityExternalId: string;
    let originalCoachProfiles: CoachProfileAPI[] = [];

    try {
      try {
        // Fetch facility data and programs/events
        const facilityResponse = await schedulingFetchGet<FacilityAPIResponse>(
          `facilities/${facilityIdParam}`,
          {},
          {},
          undefined,
          true  // public_url = true
        );

        // Handle the response as a single object (not an array)
        const facilityData = Array.isArray(facilityResponse) ? facilityResponse[0] : facilityResponse;
        facilityExternalId = facilityData.id;
        facilityName = facilityData.name;

        const facility = transformFacilityResponse(facilityData);

        originalCoachProfiles = facilityData.coachProfiles || [];
        const coaches = transformCoachesResponse(
          originalCoachProfiles,
          facilityData.name
        );

        setFacility(facility);
        setCoaches(coaches);
        setCoachProfiles(originalCoachProfiles);
      } catch (error) {
        console.error('Error loading facility data:', error);
      }

      try {
        const facilityInfoResponse = await fetchGet(`facilities/${facilityExternalId}`, {clientId: settings.API_CLIENT_ID}, {}, undefined, true);
        setFacility(prevFacility => {
          if (!prevFacility) {
            return prevFacility;
          }
          return {
            ...prevFacility,
            about: facilityInfoResponse.description || '',
            contact: {
              phone: facilityInfoResponse.phone || '',
              email: facilityInfoResponse.email || '',
              website: facilityInfoResponse.website || ''
            }
          };
        });

        // Merge coach profile data from organization API
        if (facilityInfoResponse.coachProfiles) {
          setCoaches(prevCoaches =>
            prevCoaches.map(coach => {
              const orgProfile = facilityInfoResponse.coachProfiles.find(
                (cp: any) => cp.userId === coach.id
              );

              if (orgProfile) {
                // Extract and format address components
                const formattedAddress = formatAddress(
                  orgProfile.address,
                  orgProfile.city,
                  orgProfile.country,
                  orgProfile.zip
                );

                return {
                  ...coach,
                  about: orgProfile.about || '',
                  email: orgProfile.email || '',
                  phone: orgProfile.phone || '',
                  website: orgProfile.website || '',
                  coverUrl: orgProfile.coverUrl,
                  awards: orgProfile.awards || '',
                  address: formattedAddress || '',
                };
              }

              return coach;
            })
          );
        }
      } catch (error) {
        history.push("/");
        console.error('Error loading facility data:', error);
      }

      try {
        // Fetch programs/events for this facility
        const programsResponse = await schedulingFetchGet(
          '/programs',
          { facility_id: facilityIdParam, registration_status: 'open_now_or_in_future' },
          {},
          undefined,
          true
        );

        // Transform programs to events
        const programsData = programsResponse?.data?.active || [];
        const transformedEvents = programsData.map((program: ProgramAPI) =>
          transformProgramToEvent(program, facilityName, originalCoachProfiles)
        );

        setEvents(transformedEvents);
      } catch (error) {
        console.error('Error loading programs data:', error);
        setEvents([]);  // Set empty array on error
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleAboutExpanded = () => {
    setIsAboutExpanded(!isAboutExpanded);
  };

  // CFTB-3454: is the description longer than its clamp? Measured while collapsed —
  // expanded there is nothing to compare, and the button then says "Show Less".
  useEffect(() => {
    const measure = () => {
      const element = aboutTextRef.current;
      if (!element || isAboutExpanded) {
        return;
      }
      setAboutIsClamped(element.scrollHeight > element.clientHeight + 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [facility?.about, isAboutExpanded, loading]);

  const toggleShowAllCoaches = () => {
    setShowAllCoaches(!showAllCoaches);
  };

  const toggleShowAllEvents = () => {
    setShowAllEvents(!showAllEvents);
  };

  const onClickFacilityCalendar = () => {
    // In preview mode, buttons should not be functional
    if (previewMode) {
      return;
    }
    if (facility?.publicCalendar && facility.publicCalendarUrl) {
      window.location.href = facility.publicCalendarUrl;
    }
  };


  useEffect(() => {
    if (previewMode && previewData) {
      // In preview mode, set data from props
      setFacility({
        id: 'preview',
        name: previewData.facility.name || 'Your Facility Name',
        slug: 'preview',
        logo: previewData.publicLogoUrl || undefined,
        backgroundImage: previewData.publicCoverUrl || undefined,
        about: previewData.facility.description || 'Add a description about your facility...',
        contact: {
          phone: previewData.facility.phone || '',
          email: previewData.facility.email || '',
          website: previewData.facility.website || ''
        },
        publicCalendarUrl: previewData.publicCalendarUrl || '',
        publicProgramsUrl: previewData.publicProgramsUrl || '',
        publicCalendar: previewData.publicCalendar
      });
      // Use real data from preview (no mock data)
      setCoaches(previewData.coaches || []);
      setEvents(previewData.events || []);
      setLoading(false);
    } else {
      const currentFacilityId = facilityId || '1';
      window.scrollTo(0, 0);
      setLoading(true);
      loadFacilityData(currentFacilityId);
    }
  }, [facilityId, previewMode, previewData]);

  const onClickBookNow = (calendarUUID: string) => {
    // In preview mode, buttons should not be functional
    if (previewMode) {
      return;
    }
    if (calendarUUID) {
      window.location.href = `${settings.PUBLIC_URL}/public/availability?coach=${calendarUUID}`;
    } else {
      console.warn('Public calendar URL not available');
    }
  };

  const onClickRegister = (event: Event) => {
    // In preview mode, buttons should not be functional
    if (previewMode) {
      return;
    }
    if (facility?.publicProgramsUrl) {
      window.location.href = `/public/programs/${event.id}/view`;
    } else {
      console.warn('Public programs URL not available');
    }
  };

  const renderSkeleton = () => {
    return (
      <>
        <div className="pub-facility-section">
          <div className="pub-skeleton pub-skeleton-section-title" style={{ width: '200px', height: '32px', marginBottom: '24px' }}></div>
          <div className="pub-facility-coaches-grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="pub-facility-coach-card">
                <div className="pub-skeleton" style={{ width: '100%', height: '140px', borderRadius: '8px' }}></div>
                <div style={{ marginTop: '16px' }}>
                  <div className="pub-skeleton pub-skeleton-title"></div>
                  <div className="pub-skeleton" style={{ width: '100%', height: '16px', marginTop: '12px', marginBottom: '8px' }}></div>
                  <div className="pub-skeleton" style={{ width: '100%', height: '16px', marginBottom: '8px' }}></div>
                  <div className="pub-skeleton" style={{ width: '100%', height: '16px', marginBottom: '12px' }}></div>
                  <div className="pub-skeleton pub-skeleton-description"></div>
                  <div className="pub-skeleton" style={{ width: '100%', height: '44px', marginTop: '16px', marginBottom: '12px', borderRadius: '8px' }}></div>
                  <div className="pub-skeleton" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="pub-facility-section">
          <div className="pub-skeleton pub-skeleton-section-title" style={{ width: '250px', height: '32px', marginBottom: '24px' }}></div>
          <div className="pub-facility-events-grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="pub-facility-event-card">
                <div className="pub-skeleton" style={{ width: '100%', height: '140px', borderRadius: '8px' }}></div>
                <div style={{ marginTop: '16px' }}>
                  <div className="pub-skeleton pub-skeleton-title"></div>
                  <div className="pub-skeleton" style={{ width: '60%', height: '16px', marginTop: '8px', marginBottom: '12px' }}></div>
                  <div className="pub-skeleton" style={{ width: '100%', height: '16px', marginBottom: '8px' }}></div>
                  <div className="pub-skeleton" style={{ width: '100%', height: '16px', marginBottom: '12px' }}></div>
                  <div className="pub-skeleton pub-skeleton-description"></div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px' }}>
                    <div className="pub-skeleton" style={{ width: '60px', height: '24px' }}></div>
                    <div className="pub-skeleton" style={{ width: '100px', height: '44px', borderRadius: '8px' }}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <>
        <div className="pub-header-placeholder"></div>
        <div className="pub-facility-logo-container">
          <div className="pub-facility-logo-wrapper">
            <div className="pub-facility-logo-skeleton"></div>
          </div>
        </div>
        <div className="pub-facility-container">
          <div className="pub-facility-content">
            <div className="pub-skeleton pub-skeleton-facility-title"></div>
            <div className="pub-skeleton pub-skeleton-description" style={{ width: '100%', height: '60px', marginBottom: '32px' }}></div>
            {renderSkeleton()}
          </div>
        </div>
      </>
    );
  }

  if (!facility) {
    return null;
  }

  const logo = getFacilityLogo(facility.logo);
  const noCoaches = coaches.filter(coach => coach.publicLandingPage).length;

  // One of four branding variants — see ./facilityBranding for the matrix.
  const hasBanner = hasFacilityBanner(facility.backgroundImage);
  const hasLogo = hasFacilityLogo(facility.logo);

  // Rendered over the banner when there is one, and inline on the masthead row
  // when there isn't, so `variant` picks the styling that stays legible on each.
  const renderHeaderActions = (variant: 'mobile' | 'desktop' | 'inline') => {
    const poweredByClass = `pub-powered-by pub-powered-by-${variant}`;
    return (
      <>
        {previewMode ? (
          <span className={poweredByClass}>
            {t('Powered by')} <img src={coachNowLogo} alt="CoachNow" className="pub-coachnow-logo" />
          </span>
        ) : (
          <a href="https://coachnow.io" target="_blank" rel="noopener noreferrer" className={poweredByClass}>
            {t('Powered by')} <img src={coachNowLogo} alt="CoachNow" className="pub-coachnow-logo" />
          </a>
        )}
        {/*
          No "Sign in" on the banner: the frames hide it (a hidden node in
          526:50845 and its neighbours), and a visitor books without an account.
        */}
      </>
    );
  };

  return (
    <>
      {hasBanner && (
        <div className="pub-header pub-facility-header">
          <img src={facility.backgroundImage} alt="" className="pub-header-image" />
          <div className="pub-facility-info-overlay">
            <div className="pub-facility-info-container">
              <div className="pub-header-actions">{renderHeaderActions('mobile')}</div>
            </div>
          </div>
          <div className="pub-header-overlay">
            <div className="pub-header-actions">{renderHeaderActions('desktop')}</div>
          </div>
        </div>
      )}

      <div
        className={`pub-facility-masthead ${
          hasBanner ? 'pub-facility-masthead-banner' : 'pub-facility-masthead-plain'
        }`}
      >
        {hasLogo && (
          <div className="pub-facility-logo-wrapper">
            <img src={logo} alt={facility.name} className="pub-facility-logo" />
          </div>
        )}
        <h1 className="pub-facility-name">{facility.name}</h1>
        {!hasBanner && (
          <div className="pub-facility-masthead-actions">{renderHeaderActions('inline')}</div>
        )}
      </div>

      <div className="pub-facility-container">
        <div className="pub-facility-content">
          {/* CFTB-3454: the frame rules off the masthead before the description. */}
          <hr className="pub-facility-masthead-divider" />

          <div className="pub-facility-info-row">
            <div className="pub-facility-about">
              {/*
                CFTB-3454: no "About" heading. The frame opens straight into the
                description — the facility has just named itself above — while
                "Contact information" beside it keeps its own title.
              */}
              <div className="pub-about-text-wrapper">
                <p
                  ref={aboutTextRef}
                  className={`pub-about-text ${!isAboutExpanded ? 'pub-about-text-collapsed' : ''}`}
                >
                  {facility.about}
                </p>
                {/*
                  CFTB-3454: only when there is something to read more of. This
                  offered "Read More" for any description at all, including the
                  one-word ones the report shows, because it asked whether there was
                  text rather than whether the text was clipped.
                */}
                {(aboutIsClamped || isAboutExpanded) && (
                  <button className="pub-view-all-link" onClick={toggleAboutExpanded}>
                    {isAboutExpanded ? t('Show Less') : t('Read More')}
                  </button>
                )}
              </div>
            </div>

            {(facility.contact?.phone || facility.contact?.email || facility.contact?.website || facility.fullAddress) && (
              <div className="pub-facility-contact-section">
                <h3 className="pub-section-title">{t('Contact Information')}</h3>
                <div className="pub-facility-contact-items">
                  {facility.contact?.phone && (
                    <div className="pub-contact-item">
                      <div className="pub-contact-icon-wrapper">
                        <img src={phoneIcon} alt="Phone" className="pub-contact-icon" />
                      </div>
                      {previewMode ? (
                        <span className="pub-contact-text">
                          {formatPhoneNumber(facility.contact?.phone)}
                        </span>
                      ) : (
                        <a href={`tel:${facility.contact?.phone}`} className="pub-contact-text">
                          {formatPhoneNumber(facility.contact?.phone)}
                        </a>
                      )}
                    </div>
                  )}
                  {facility.contact?.email && (
                    <div className="pub-contact-item">
                      <div className="pub-contact-icon-wrapper">
                        <img src={mailIcon} alt="Email" className="pub-contact-icon" />
                      </div>
                      {previewMode ? (
                        <span className="pub-contact-text">
                          {facility.contact?.email}
                        </span>
                      ) : (
                        <a href={`mailto:${facility.contact?.email}`} className="pub-contact-text">
                          {facility.contact?.email}
                        </a>
                      )}
                    </div>
                  )}
                  {facility.contact?.website && (
                    <div className="pub-contact-item">
                      <div className="pub-contact-icon-wrapper pub-contact-icon-wrapper-link">
                        <img src={linkIcon} alt="Website" className="pub-contact-icon" />
                      </div>
                      {previewMode ? (
                        <span className="pub-contact-link">
                          {facility.contact?.website}
                        </span>
                      ) : (
                        <a href={facility.contact?.website?.match(/^https?:\/\//) ? facility.contact.website : `https://${facility.contact?.website}`} className="pub-contact-link">
                          {facility.contact?.website}
                        </a>
                      )}
                    </div>
                  )}
                  {facility.fullAddress && (
                    <div className="pub-contact-item">
                      <div className="pub-contact-icon-wrapper">
                        <img src={locationIcon} alt="Address" className="pub-contact-icon" />
                      </div>
                      {previewMode ? (
                        <span className="pub-contact-text">
                          {facility.fullAddress}
                        </span>
                      ) : (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(facility.fullAddress)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="pub-contact-text"
                        >
                          {facility.fullAddress}
                        </a>
                      )}
                    </div>
                  )}
                </div>
                {facility.publicCalendar && facility.publicCalendarUrl && (
                  <Button
                    buttonType="secondary-filled"
                    buttonStyles="pub-facility-calendar-btn"
                    title={t('View Facility Calendar')}
                    onClickFunc={onClickFacilityCalendar}
                  />
                )}
              </div>
            )}
          </div>

          <div className="horizontal-line" />

          <div className="pub-facility-section">
            {noCoaches > 0 && ( <div className="pub-section-header">
              <h2 className="pub-section-title">{t('Our Coaches')} ({noCoaches})</h2>
              {noCoaches > 6 && (
                <a
                  href="#"
                  className="pub-view-all-link"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleShowAllCoaches();
                  }}
                >
                  {showAllCoaches ? t('Show Less') : t('View All')}
                </a>
              )}
            </div> )}
            {coaches.length > 0 ? (
              <div className="pub-facility-coaches-grid">
                {coaches.filter(coach => coach.publicLandingPage).slice(0, showAllCoaches ? coaches.length : 6).map((coach) => {
                  // Find the original coach profile to get lessonRates
                  const originalProfile = coachProfiles.find(cp => cp.externalId === coach.id);
                  const lessonRates = originalProfile?.lessonRates || [];

                  return (
                  <div key={coach.id} className="pub-facility-coach-card">
                    <Link
                      to={{
                        pathname: `/facility/${facility.slug}/coach/${coach.username}/${coach.id}`,
                        state: {
                          coach,
                          facilityId: facility.id,
                          facilityName: facility.name,
                          facilitySlug: facility.slug,
                          facilityLogo: facility.logo,
                          facilityBackgroundImage: facility.backgroundImage,
                          facilityPublicLandingPage: facility.publicLandingPage,
                          lessonRates, events,
                          publicCalendarUrl: `${settings.PUBLIC_URL}/public/availability?coach=${coach.calendarUuid}`,
                          publicProgramsUrl: facility.publicProgramsUrl
                        }
                      }}
                      className="pub-facility-coach-image-wrapper"
                    >
                      <img
                        src={getCoachAvatar(coach.avatar)}
                        alt={coach.name}
                        className="pub-facility-coach-image"
                      />
                    </Link>
                    <div className="pub-facility-coach-info">
                      <h3 className="pub-facility-coach-name">{coach.name}</h3>
                      <div className="pub-facility-coach-meta">
                        <div className="pub-facility-coach-location">
                          <img src={locationIcon} alt="Location" className="pub-facility-coach-icon" />
                          <span>{coach.location}</span>
                        </div>
                        <div className="pub-facility-coach-session">
                          <img src={timeIcon} alt="Time" className="pub-facility-coach-icon" />
                          <span>{coach.nextSession}</span>
                        </div>
                        {/* CN-2711: null means there is nothing to quote —
                            every bookable option hides its price, or there are
                            none. A free option still quotes "From $0". */}
                        {coach.priceFrom !== null && (
                          <div className="pub-facility-coach-price">
                            <img src={moneyIcon} alt="Price" className="pub-facility-coach-icon" />
                            <span>From {generateCurrencySymbol(coach.currency)} {coach.priceFrom}</span>
                          </div>
                        )}
                      </div>
                      <hr className="pub-facility-divider" />
                      <p className="pub-facility-coach-description">{coach.about}</p>
                      <div className="pub-coach-buttons">
                        {coach.publicCalendar === true && (
                          <Button buttonType="secondary-filled" buttonStyles="pub-book-now-btn" title={t('Book Now')} onClickFunc={() => onClickBookNow(coach.calendarUuid)} />
                        )}
                        {previewMode ? (
                          <Button buttonType="secondary-filled" buttonStyles="pub-view-services-btn" title={t('View All Services')} onClickFunc={() => {}} />
                        ) : (
                          <Link
                            to={{
                              pathname: `/facility/${facility.slug}/coach/${coach.username}/${coach.id}`,
                              state: {
                                coach,
                                facilityId: facility.id,
                                facilityName: facility.name,
                                facilitySlug: facility.slug,
                                facilityLogo: facility.logo,
                                facilityBackgroundImage: facility.backgroundImage,
                                facilityPublicLandingPage: facility.publicLandingPage,
                                lessonRates, events,
                                publicCalendarUrl: `${settings.PUBLIC_URL}/public/availability?coach=${coach.calendarUuid}`,
                                publicProgramsUrl: facility.publicProgramsUrl,
                              }
                            }}
                          >
                            <Button buttonType="secondary-filled" buttonStyles="pub-view-services-btn" title={t('View All Services')} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="pub-empty-state">
                <div className="pub-empty-state-icon">👤</div>
                <h3 className="pub-empty-state-title">{t('No coaches available')}</h3>
                <p className="pub-empty-state-description">{t('Check back later for available coaches.')}</p>
              </div>
            )}
          </div>

          <div className="pub-facility-section">
            <div className="pub-section-header">
              <h2 className="pub-section-title">{t('Events & Clinics')}</h2>
              {events.length > 6 && (
                <a
                  href="#"
                  className="pub-view-all-link"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleShowAllEvents();
                  }}
                >
                  {showAllEvents ? t('Show Less') : t('View All')}
                </a>
              )}
            </div>
            {events.length > 0 ? (
              <div className="pub-facility-events-grid">
                {events.slice(0, showAllEvents ? events.length : 6).map((event) => (
                  <ProgramCard
                    key={event.id}
                    event={event}
                    facility={facility}
                    getCoachAvatar={getCoachAvatar}
                    onClickRegister={onClickRegister}
                  />
                ))}
              </div>
            ) : (
              <div className="pub-empty-state">
                <div className="pub-empty-state-icon">📅</div>
                <h3 className="pub-empty-state-title">{t('No events available')}</h3>
                <p className="pub-empty-state-description">{t('Check back later for upcoming events and clinics.')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FacilityPage;
